import { type CSSProperties, useEffect, useState } from 'react';
import { Dataroom } from '@vcip/design-system';
import { useT } from '@/i18n/runtime';
import { useSessionStore } from '@/store/sessionStore';
import { deriveBrandTokens } from '@/theme/brand';
import { useBrandedDocumentTitle } from '@/theme/documentTitle';
import { resolveTenant } from '@/theme/servedTenant';
import { checkKeyOfStep, isCheckStepId } from '@/types';
import { LocaleSwitcher } from '@/i18n/LocaleSwitcher';
import { PoweredByKycHub } from './components/PoweredByKycHub';
import { SupportAffordance } from './components/SupportAffordance';
import { STEP_COMPONENTS } from './steps';
import { checkComponentFor } from './steps/checkRegistry';
import { isMockMode } from '@/api/client';
import { bootstrapPlan } from './planBootstrap';

/**
 * The mobile-first app shell. Renders the design-system <Dataroom> (the
 * `.droom[data-brand]` white-label container) and drives the current step from
 * the flow machine. Kept thin: each step renders its own app bar / stepper.
 *
 * ★ WHITE-LABEL — ONE mechanism. <Dataroom brand> sets the `data-brand` slug
 * (the package's static [data-brand] fallback for SSR / no-JS), and the five
 * derived `--brand*` custom properties are written inline (inline wins over the
 * static block). So ANY tenant — a known slug or a config arriving live from the
 * session API — reskins the entire flow from ONE brand colour, with no component
 * ever hardcoding a brand hex. `deriveBrandTokens` is the single derivation.
 */
/**
 * Steps that make session-scoped API calls — they must NOT render until the real
 * sessionId is in the store, or they fire requests against a placeholder id (the
 * `sess_demo` leak surfaced in a live drop-test: `Invalid UUID string: sess_demo`).
 * The route seeds the id in an effect, so there is a first-paint gap this gate closes.
 * welcome/complete carry no session calls and render freely.
 */
const SESSION_SCOPED_STEPS = new Set(['schedule', 'consent', 'confirm', 'aadhaar', 'capture', 'waiting', 'call']);

export function FlowShell(): JSX.Element {
  const { t } = useT();
  const currentStep = useSessionStore((s) => s.session.currentStep);
  const sessionId = useSessionStore((s) => s.session.sessionId);
  const tenantId = useSessionStore((s) => s.session.tenantId);
  const servedBranding = useSessionStore((s) => s.servedBranding);
  const planState = useSessionStore((s) => s.planState);
  // Item 4 pin: SERVED branding wins over the local fallback registry; the
  // registry is the fallback only when the server said nothing (older be).
  const sessionRegime = useSessionStore((s) => s.session.regime);
  // 19-Aug: the regime is SESSION truth (GET /sessions/{id}), never the local theme registry —
  // an IFSCA journey on an RBI-defaulted theme said "RBI Video-KYC" on the consent step.
  const tenant = resolveTenant(tenantId, servedBranding, sessionRegime);
  // The browser tab carries the tenant ONLY when the server named one — the
  // dev registry's fallback names never reach a customer's tab.
  useBrandedDocumentTitle(servedBranding?.name);

  // Plan-derived check steps (`check:${CheckKey}`) resolve via the check registry;
  // frame steps via the legacy component map. An unregistered key → MissingCheckStep.
  const StepComponent = isCheckStepId(currentStep)
    ? checkComponentFor(checkKeyOfStep(currentStep))
    : STEP_COMPONENTS[currentStep];
  // Check steps are session-scoped (they make session-scoped API calls) too.
  const awaitingSession =
    (SESSION_SCOPED_STEPS.has(currentStep) || isCheckStepId(currentStep)) && !sessionId;

  // 18-Aug RCA (owner item #2): a REQUIRED check must never be silently downgraded because the
  // frozen plan could not be read. When the plan is UNKNOWN (the read failed / came back empty
  // against a real backend) and the customer is past the entry screens, BLOCK with an honest retry
  // rather than running the hardcoded legacy flow. Mock mode always has a known (demo) plan.
  const planBlocked =
    !isMockMode() &&
    planState === 'unknown' &&
    (SESSION_SCOPED_STEPS.has(currentStep) || isCheckStepId(currentStep)) &&
    currentStep !== 'welcome';
  const retryPlan = () => {
    if (sessionId) void bootstrapPlan(sessionId);
  };

  // KYCID·00001-00003-00003: the "Preparing…" gate must never hang. If a sessionId
  // has not resolved within a bounded window (the bare-domain no-invite case, or a
  // failed invite read), fall through to an honest, actionable state — never an
  // indefinite spinner.
  const PREPARE_TIMEOUT_MS = 8_000;
  const [prepareTimedOut, setPrepareTimedOut] = useState(false);
  useEffect(() => {
    if (!awaitingSession) {
      setPrepareTimedOut(false);
      return undefined;
    }
    const t = window.setTimeout(() => setPrepareTimedOut(true), PREPARE_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [awaitingSession]);
  // Custom properties aren't part of the CSSProperties type; cast via unknown.
  const brandStyle = deriveBrandTokens(tenant.branding) as unknown as CSSProperties;

  return (
    <Dataroom brand={tenant.id} style={brandStyle}>
      <main className="app-screen" aria-label={`${tenant.name} · Video-KYC`}>
        {planBlocked ? (
          <div className="app-entry-error" role="alert">
            <div className="app-entry-title">{t('droom.shell.planError.title')}</div>
            <div className="app-entry-body">{t('droom.shell.planError.body')}</div>
            <button type="button" className="app-entry-retry" onClick={retryPlan}>
              {t('droom.shell.planError.retry')}
            </button>
          </div>
        ) : awaitingSession && prepareTimedOut ? (
          <div className="app-entry-error" role="alert">
            <div className="app-entry-title">{t('droom.shell.entryError.title')}</div>
            <div className="app-entry-body">{t('droom.shell.entryError.body')}</div>
            <button
              type="button"
              className="app-entry-retry"
              onClick={() => window.location.reload()}
            >
              {t('droom.shell.entryError.retry')}
            </button>
          </div>
        ) : awaitingSession ? (
          <div className="app-preparing" role="status">
            {t('droom.shell.preparing')}
          </div>
        ) : (
          <>
            <StepComponent tenant={tenant} />
            {/* Tier 1 #14 — the route to a human, on EVERY step. Mounted in the
                shell rather than per-step so a new step cannot ship without it,
                which is how ~17 screens came to have none. Inline disclosure,
                never a modal: one-shot mobile needs per-step disclosure, and a
                focus-trapping sheet over a capture screen would repeat the
                officer-console mistake on the customer. */}
            <SupportAffordance tenant={tenant} step={currentStep} />
            {/* DEC-053 — the language choice, on every step except the live call
                (mid-call, the officer guides; a language flip mid-recording is
                a support conversation, not a toggle). One visible tap. */}
            {currentStep !== 'call' ? <LocaleSwitcher /> : null}
          </>
        )}
        {/* Owner round 2026-08-28 item 4 — the platform credit, OUTSIDE the
            step conditional so no shell state (blocked / preparing / error /
            any step, the live call included) can render a screen without it. */}
        <PoweredByKycHub />
      </main>
    </Dataroom>
  );
}
